import {
	CALLBACK_PATH,
	configureOAuth,
	createAuthorizationUrl,
	finalizeAuthorization,
	getOAuthClientConfig,
	isOAuthConfigured,
	LoginError,
	OAUTH_SCOPE,
	resolveFromIdentity,
	type OAuthClientConfig,
	type Session,
} from '~/api/oauth';

let clientConfig: OAuthClientConfig | undefined;

/**
 * Configure the OAuth client for the current environment. Idempotent; safe to
 * call repeatedly. Must run before any OAuth account is resumed.
 */
export const initOAuth = (): OAuthClientConfig => {
	if (!clientConfig) {
		clientConfig = getOAuthClientConfig();

		if (!isOAuthConfigured()) {
			configureOAuth({
				metadata: {
					client_id: clientConfig.clientId,
					redirect_uri: clientConfig.redirectUri,
				},
				storageName: 'oauth',
			});
		}
	}

	return clientConfig;
};

/**
 * Resolve an identifier (handle or DID) to its PDS and begin the OAuth
 * authorization flow, redirecting the browser to the user's authorization
 * server. Never resolves on success (the page navigates away).
 */
export const startOAuthLogin = async (identifier: string, signal?: AbortSignal): Promise<never> => {
	initOAuth();

	const { identity, metadata } = await resolveFromIdentity(identifier.trim(), signal);

	signal?.throwIfAborted();

	const authUrl = await createAuthorizationUrl({
		metadata: metadata,
		identity: identity,
		scope: OAUTH_SCOPE,
	});

	signal?.throwIfAborted();

	location.assign(authUrl.toString());

	// Block the caller until the navigation takes effect.
	await new Promise(() => {});
	throw new Error(`unreachable`);
};

/**
 * Whether the current page load looks like an OAuth redirect callback.
 */
export const isOAuthCallback = (): boolean => {
	return location.pathname === CALLBACK_PATH && readCallbackParams() !== null;
};

const readCallbackParams = (): URLSearchParams | null => {
	// We request `response_mode=fragment`, so parameters arrive in the hash.
	const hash = location.hash.startsWith('#') ? location.hash.slice(1) : '';
	if (hash) {
		const params = new URLSearchParams(hash);
		if (params.has('state')) {
			return params;
		}
	}

	// Some authorization servers fall back to the query string.
	const search = new URLSearchParams(location.search);
	if (search.has('state')) {
		return search;
	}

	return null;
};

/**
 * Complete the OAuth flow from the redirect callback, returning the resulting
 * session. The caller is responsible for registering it with the multiagent.
 */
export const completeOAuthCallback = async (): Promise<Session> => {
	initOAuth();

	const params = readCallbackParams();
	if (!params) {
		throw new LoginError(`missing callback parameters`);
	}

	return finalizeAuthorization(params);
};
